import { Box, CircularProgress, Typography } from '@mui/material'
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate, useParams } from '@tanstack/react-router'
import axios from 'axios';
import { useCandidateStore } from 'store/candidateStore';

import WatchIcon from '@mui/icons-material/Watch';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import DoneAllOutlinedIcon from '@mui/icons-material/DoneAllOutlined';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined';


import SmallWatch from "assets/images/history-table/small-watch.png";
import NoData from 'icons/NoData.icon';
import reviewStore from 'store/reviewStore';

export const Route = createFileRoute(
  '/(partner)/_partner/single-interview',
)({
  component: RouteComponent,
})


// Define candidate status enum
enum CandidateStatus {
  NotRun = 0,
  InProgress = 1,
  Completed = 2,
  Passed = 3,
  Failed = 4
}

// Define the candidate data types
interface Candidate {
  submittedInterviewId: string;
  candidateName: string;
  completedOnUtc: string;
  forReview: number;
  pickAndChooseCorrect: number | null;
  pickAndChooseTotal: number;
  status: CandidateStatus;
  totalTime: number;
  deadline: string;
}

function RouteComponent() {
  const navigate = useNavigate();

  // Prikupljamo Id iz stor-a za interview ID
  const {reviewData, updateReviewData} = reviewStore()


  const { setSelectedCandidate } = useCandidateStore();
    
    
  // Prikupljamo kandidate za taj intervju
  const { data: candidates, isLoading, error } = useQuery({
    queryKey: ['candidates', reviewData.interviewId],
    queryFn: async (): Promise<Candidate[]> => {
      const token = localStorage.getItem('token');
      const response = await axios.get(`https://interviewer.runasp.net/api/interviews/${reviewData.interviewId}/candidates`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    },
    enabled: !!reviewData.interviewId,
  });

    // Helper function to get status text
    const getStatusText = (status: CandidateStatus): string => {
      switch (status) {
        case CandidateStatus.NotRun:
          return 'Not Run';
        case CandidateStatus.InProgress:
          return 'In Progress';
        case CandidateStatus.Completed:
          return 'Completed';
        case CandidateStatus.Passed:
          return 'Passed';
        case CandidateStatus.Failed:
          return 'Failed';
        default:
          return 'Unknown';
      }
    };

    // Helper function to get status color
    const getStatusColor = (status: CandidateStatus): string => {
      switch (status) {
        case CandidateStatus.NotRun:
          return 'var(--Interviewer-Lightest-Black)';
        case CandidateStatus.InProgress:
          return 'var(--Interviewer-Green)';
        case CandidateStatus.Completed:
          return 'var(--Interviewer-Black)';
        case CandidateStatus.Passed:
          return 'var(--Interviewer-Green)';
        case CandidateStatus.Failed:
          return 'var(--Red-Error-Color)';
        default:
          return 'var(--Interviewer-Black)';
      }
    };

    // Helper function to format time
    const formatTime = (seconds: number): string => {
      if (seconds === 0) return '-';
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      return `${minutes}m ${remainingSeconds}s`;
    };

    // Helper function to format a date string into a readable form
    // const formatDateReadable = (isoDateString: string): string => {
    //   if (!isoDateString || isoDateString.startsWith('0001-')) return '-';
    //   const date = new Date(isoDateString);
    //   if (isNaN(date.getTime())) return '-';
    //   return date.toLocaleDateString(undefined, {
    //     day: 'numeric',
    //     month: 'long',
    //     year: 'numeric',
    //   });
    // };

    const timeOfCompleted = (time:any) => {
        if (!time) return "";

        const date = new Date(time);

        // Handle invalid or placeholder dates
        if (isNaN(date.getTime()) || date.getFullYear() === 1) {
          return "—";
        }

        return date.toLocaleDateString("en-US", {
          day: "numeric",
          month: "long",
          year: "numeric",
        });
    }

    const timeLeft = (deadline: any) => {
      const now = new Date();
      const end = new Date(deadline);
      const diffMs = end.getTime() - now.getTime();

      if (diffMs <= 0) {
        return "Time’s up";
      }

      const totalSeconds = Math.floor(diffMs / 1000);
      const totalMinutes = Math.floor(totalSeconds / 60);
      const totalHours = Math.floor(totalMinutes / 60);
      const days = Math.floor(totalHours / 24);
      const hours = totalHours % 24;
      const minutes = totalMinutes % 60;

      if (days > 0 && hours > 0) return `${days} day${days > 1 ? "s" : ""}, ${hours} hour${hours > 1 ? "s" : ""}`;
      if (days > 0) return `${days} day${days > 1 ? "s" : ""}`;
      if (hours > 0 && minutes > 0) return `${hours} hour${hours > 1 ? "s" : ""}, ${minutes} minute${minutes > 1 ? "s" : ""}`;
      if (hours > 0) return `${hours} hour${hours > 1 ? "s" : ""}`;
      if (minutes > 0) return `${minutes} minute${minutes > 1 ? "s" : ""}`;
      
      return "Less than a minute";
    }

    // Determine color for Pick/Choose value (red if not perfect, green if perfect)
    const getPickChooseColor = (correct: number | null, total: number): string => {
      const normalizedCorrect = correct ?? 0;
      if (!total || total === 0) return 'var(--Interviewer-Lightest-Black)';
      return normalizedCorrect < total ? 'var(--Red-Error-Color)' : 'var(--Interviewer-Green)';
    };

    // Calculate statistics
    const totalCandidates = candidates?.length || 0;
    const forReviewCount = candidates?.filter(c => c.forReview > 0).length || 0;
    const passedCount = candidates?.filter(c => c.status === CandidateStatus.Passed).length || 0;
    const failedCount = candidates?.filter(c => c.status === CandidateStatus.Failed).length || 0;

    return (
        <Box
          sx={{
            padding: {xs: '16px', md: '32px'},
          }}
        >
            {isLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
              </Box>
            )}
            
            {error && (
               <NoData />
            )}
            
            {candidates && (
              <>
                <Box
                  sx={{
                    display: 'grid',
                    gap: 2,
                    gridTemplateColumns: {
                      xs: '1fr',
                      sm: 'repeat(2, 1fr)',
                      md: 'repeat(4, 1fr)',
                      lg: 'repeat(6, 1fr)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      gridColumn: { xs: 'span 1', lg: 'span 2' },
                      bgcolor: 'white',
                      borderRadius: '12px',
                      p: '24px',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <img src={SmallWatch} alt="Icon" />

                      <Typography
                        sx={{
                          fontSize: '14px',
                          fontWeight: '600',
                          color: 'var(--Interviewer-Lightest-Black)',
                        }}
                      >
                        Time Left
                      </Typography>
                    </Box>
                    <Typography
                      sx={{
                        pt: '8px',
                        fontWeight: '700',
                        fontSize: '24px',
                        color: 'var(--Interviewer-Black)',
                      }}
                    >
                      3 days, 1 hour - ?
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      gridColumn: { xs: 'span 1', lg: 'span 2' },
                      bgcolor: 'white',
                      borderRadius: '12px',
                      p: '24px',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <PersonOutlineOutlinedIcon color='primary' />

                      <Typography
                        sx={{
                          fontSize: '14px',
                          fontWeight: '600',
                          color: 'var(--Interviewer-Lightest-Black)',
                        }}
                      >
                        Candidates
                      </Typography>
                    </Box>
                    <Typography
                      sx={{
                        pt: '8px',
                        fontWeight: '700',
                        fontSize: '24px',
                        color: 'var(--Interviewer-Black)',
                      }}
                    >
                      {totalCandidates}
                    </Typography>
                  </Box>

                  <Box sx={{ bgcolor: 'white', borderRadius: '12px', p: '24px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <PersonOutlineOutlinedIcon color='primary' />

                      <Typography
                        sx={{
                          fontSize: '14px',
                          fontWeight: '600',
                          color: 'var(--Interviewer-Lightest-Black)',
                        }}
                      >
                        For review
                      </Typography>
                    </Box>
                    <Typography
                      sx={{
                          pt: '8px',
                          fontWeight: '700',
                          fontSize: '24px',
                          color: 'var(--Interviewer-Black)',
                        }}
                      >
                      {forReviewCount}
                    </Typography>
                  </Box>

                  <Box sx={{ bgcolor: 'white', borderRadius: '12px', p: '24px' }}>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <CloseOutlinedIcon sx={{color: 'var(--Red-Error-Color)'}}/>

                      <Typography
                        sx={{
                          fontSize: '14px',
                          fontWeight: '600',
                          color: 'var(--Interviewer-Lightest-Black)',
                        }}
                      >
                        Failed
                      </Typography>
                    </Box>
                    <Typography
                      sx={{
                          pt: '8px',
                          fontWeight: '700',
                          fontSize: '24px',
                          color: 'var(--Interviewer-Black)',
                        }}
                      >
                      {failedCount}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ bgcolor: 'white', borderRadius: '12px', p: '24px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <DoneAllOutlinedIcon sx={{color: 'var(--Interviewer-Green)'}} />

                      <Typography
                        sx={{
                          fontSize: '14px',
                          fontWeight: '600',
                          color: 'var(--Interviewer-Lightest-Black)',
                        }}
                      >
                        Passed
                      </Typography>
                    </Box>
                    <Typography
                      sx={{
                          pt: '8px',
                          fontWeight: '700',
                          fontSize: '24px',
                          color: 'var(--Interviewer-Black)',
                        }}
                      >
                      {passedCount}
                    </Typography>
                  </Box>
                </Box>
                <Box>
                  <Typography variant='h1' sx={{pt: '32px', pb: '16px'}}>List of Candidates</Typography>
                  {candidates.map((candidate) => (
                    <Box 
                      key={candidate.submittedInterviewId} 
                      onClick={() => {
                        // setSelectedCandidate(candidate, interviewId || '');
                        navigate({ 
                          to: '/interview-overview',
                        });
                        updateReviewData({
                          candidateId: candidate.submittedInterviewId
                        })
                      }}
                      sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        flexWrap: { xs: 'wrap', sm: 'nowrap' },
                        justifyContent: 'space-between',
                        background: '#fff',
                        borderRadius: '12px',
                        border: '1px solid var(--Light-Gray)',
                        boxShadow: '0 1px 2px 0 rgba(2,5,17,0.06)',
                        p: '12px',
                        gap: { xs: '10px', sm: '5px' },
                        width: '100%',
                        mb: '8px',
                        cursor: 'pointer',
                        transition: 'box-shadow 0.2s ease, transform 0.2s ease',
                        '&:hover': {
                          boxShadow: '0 4px 10px rgba(2,5,17,0.12)',
                          transform: 'translateY(-1px)',
                        },
                      }}
                    >
                      {/* Candidate Name Column */}
                      <Box sx={{
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '8px',
                        minWidth: { xs: '100%', sm: '130px' },
                        maxWidth: { xs: '100%', sm: '130px' },
                        flex: { xs: '1 1 100%', sm: '0 0 210px' }
                      }}>
                        <Box sx={{padding: '8px', border: '1px solid var(--Light-Gray)', width: 'fit-content', height: 'fit-content', borderRadius: '100%'}}>
                          <PersonOutlineOutlinedIcon />
                        </Box>
                        <Typography
                          sx={{
                            fontWeight: 600,
                            fontSize: { xs: '13px', sm: '14px' },
                            color: 'var(--Interviewer-Black)',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {candidate.candidateName}
                        </Typography>
                      </Box>

                      {/* Finished on Column */}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          minWidth: { xs: '48%', sm: '180px' },
                          maxWidth: { xs: '48%', sm: '180px' },
                          flex: { xs: '1 1 48%', sm: '0 0 220px' },
                          mt: { xs: 1, sm: 0 },
                        }}
                      >
                        <Typography sx={{ color: 'var(--Interviewer-Lightest-Black)', fontWeight: 500, fontSize: { xs: '11px', sm: '12px' } }}>
                          Finished on
                        </Typography>
                        <Typography sx={{ fontWeight: 600, fontSize: { xs: '13px', sm: '14px' }, color: 'var(--Interviewer-Black)' }}>
                          {timeOfCompleted(candidate.completedOnUtc)}
                        </Typography>
                      </Box>

                      {/* Time left Column */}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          minWidth: { xs: '48%', sm: '180px' },
                          maxWidth: { xs: '48%', sm: '180px' },
                          flex: { xs: '1 1 48%', sm: '0 0 170px' },
                          mt: { xs: 1, sm: 0 },
                        }}
                      >
                        <WatchIcon sx={{ color: 'var(--Interviewer-Lightest-Black)', width: '16px', height: '16px' }} />
                        <Typography sx={{ color: 'var(--Interviewer-Lightest-Black)', fontWeight: 500, fontSize: { xs: '11px', sm: '12px' } }}>
                          Time left
                        </Typography>
                        <Typography sx={{ fontWeight: 700, fontSize: { xs: '13px', sm: '14px' }, color: 'var(--Interviewer-Black)' }}>
                          {timeLeft(candidate.deadline)}
                        </Typography>
                      </Box>

                      {/* Pick/Choose Q Column */}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          minWidth: { xs: '48%', sm: '150px' },
                          maxWidth: { xs: '48%', sm: '150px' },
                          flex: { xs: '1 1 48%', sm: '0 0 150px' },
                          mt: { xs: 1, sm: 0 },
                        }}
                      >
                        <CheckBoxOutlinedIcon fontSize='small' sx={{color: 'var(--Interviewer-Lightest-Black)', width: '16px', height: '16px'}}/>
                        <Typography sx={{ color: 'var(--Interviewer-Lightest-Black)', fontWeight: 500, fontSize: { xs: '11px', sm: '12px' } }}>
                          Pick/Choose Q
                        </Typography>
                        <Typography sx={{ fontWeight: 700, fontSize: { xs: '13px', sm: '14px' }, color: getPickChooseColor(candidate.pickAndChooseCorrect, candidate.pickAndChooseTotal) }}>
                          {candidate.pickAndChooseTotal && candidate.pickAndChooseTotal > 0
                            ? `${candidate.pickAndChooseCorrect ?? 0}/${candidate.pickAndChooseTotal}`
                            : '-'}
                        </Typography>
                      </Box>

                      {/* For review Q Column */}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          minWidth: { xs: '48%', sm: '130px' },
                          maxWidth: { xs: '48%', sm: '130px' },
                          flex: { xs: '1 1 48%', sm: '0 0 130px' },
                          mt: { xs: 1, sm: 0 },
                        }}
                      >
                        <RemoveRedEyeOutlinedIcon sx={{ color: 'var(--Interviewer-Green)', width: '16px', height: '16px' }} />
                        <Typography sx={{ color: 'var(--Interviewer-Lightest-Black)', fontWeight: 500, fontSize: { xs: '11px', sm: '12px' } }}>
                          For review Q
                        </Typography>
                        <Typography sx={{ fontWeight: 700, fontSize: { xs: '13px', sm: '14px' }, color: 'var(--Interviewer-Black)' }}>
                          {candidate.forReview}
                        </Typography>
                      </Box>

                      {/* Status Column */}
                      <Box sx={{
                        minWidth: { xs: '48%', sm: '120px' },
                        maxWidth: { xs: '48%', sm: '120px' },
                        flex: { xs: '1 1 48%', sm: '0 0 120px' },
                        display: 'flex',
                        justifyContent: 'flex-start',
                        mt: { xs: 1, sm: 0 },
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <Box sx={{ backgroundColor: getStatusColor(candidate.status), py: '6px', px: '14px', borderRadius: '999px', width: 'fit-content', display: 'flex', alignItems: 'center' }}>
                            <Typography sx={{ color: 'white', fontWeight: 700, fontSize: { xs: '12px', sm: '14px' } }}>
                              {getStatusText(candidate.status)}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>

                      <KeyboardArrowRightIcon sx={{ color: 'var(--Interviewer-Black)', display: { xs: 'none', sm: 'inline-flex' } }} />
                    </Box>
                  ))}
                </Box>
              </>
            )}

        </Box>
    )
}
